import { type ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { format } from 'app/shared/utils/string.util';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { type IDataflowTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { ReadMode } from 'app/model/dataflow/dataset-settings/sap-odp-settings';

const dataflowSapODataSource: IDataflowTypeMetadata = {
    type: DatasetFormatType.SapOData,
    settings: adfModelTypeMap.sapOData.dataflowSettings,
    controls: [
        {
            type: 'dataflow-schema-table-selection',
            bind: 'servicePath',
            schemaNameLabel: stringResources.ServicePath_Label,
            schemaNamePlaceholder: format(
                stringResources.DataFlowSchemaTableSelectionSchemaPlaceholder,
                stringResources.ServicePath_Label
            ),
            schemaRequired: true,
            selectionType: 'schema',
            enableCache: false,
            browseSettings: {
                includedSettingsKeys: ['browseServicePath'],
                schemaLabelKey: 'displayName',
                schemaValueKey: 'schemaName',
                schemaDescriptionKey: 'description'
            }
        },
        {
            type: 'dataflow-schema-table-selection',
            bind: 'entityName',
            tableNameLabel: stringResources.TableNameDisplay_EntityName,
            tableNamePlaceholder: format(
                stringResources.DataFlowSchemaTableSelectionTablePlaceholder,
                stringResources.ServicePath_Label
            ),
            tableRequired: true,
            selectionType: 'table',
            enableCache: false,
            browseSettings: {
                includedSettingsKeys: ['servicePath'],
                tableLabelKey: 'tableName',
                tableValueKey: 'tableName'
            }
        },
        {
            type: 'parameterizable',
            child: {
                type: 'select',
                bind: 'readMode',
                label: stringResources.DataFlowRunMode,
                options: [
                    {
                        label: stringResources.DataFlowFullOnFirstRunThenIncremental,
                        value: ReadMode.FULL_AND_INCREMENTAL_LOAD
                    },
                    { label: stringResources.DataFlowFullOnEveryRun, value: ReadMode.FULL_LOAD },
                    {
                        label: stringResources.DataFlowIncrementalChangesOnly,
                        value: ReadMode.INCREMENTAL_LOAD,
                        disabled: true,
                        tooltip: stringResources.DataFlowIncrementalChangesOnlyDisabledTooltip
                    }
                ],
                isDataFlowExpression: true
            },
            showNavigationMethod: 'hover',
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox-number',
                bind: 'batchSize',
                label: stringResources.ModelName_BatchSize,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox-number',
                bind: 'timeout',
                label: stringResources.Timeout,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true
        },
        {
            type: 'sap-odp-key-columns',
            bind: 'sapODataKeyColumns',
            required: true,
            hide: `{ readMode.expression === 'fullLoad' }`
        }
    ]
};

export const sapODataMetadata: ILinkedServiceTypeMetadata = {
    type: adfModelTypeMap.sapOData.linkedServiceType,
    displayName: format(stringResources.ModelName_DisplayTextPreviewTemplate, stringResources.SapOData_DisplayText),
    linkedServiceKind: 'table',
    icon: {
        className: 'svg-i-100x100-Sap'
    },
    // isOnPremises: true,
    isSHIRAllowedInDataFlow: true,
    // supportIRVersion: undefined, // TODO: add IR version
    supportDataFlowSource: true,
    // supportDatasetV2: true,
    supportFirstClassParameterExperience: true,
    // fetchNestedNavTable: true,
    dataflowSource: [dataflowSapODataSource],
    isFirstClass: true,
    copySourceTypes: [adfModelTypeMap.sapOData.copySourceType],
    copySinkTypes: [],
    onetimeQueryTemplate: '',
    timedQueryTemplate: '',
    category: ['Database'],
    controls: [
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'hostUrl',
                label: stringResources.ModelName_Host,
                required: true,
                validators: [{ type: 'required', errorMessage: '' }],
                isLabelVertical: true
            }
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'catalogServicePath',
                label: stringResources.ModelName_CatalogServicePath,
                defaultValue: '/sap/opu/odata/IWFND/CATALOGSERVICE;v=2/',
                isLabelVertical: true
            }
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'userName',
                label: stringResources.ModelName_UserName,
                required: true,
                validators: [{ type: 'required', errorMessage: '' }],
                isLabelVertical: true
            }
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'language',
                label: stringResources.Language,
                isLabelVertical: true
            }
        },
        {
            type: 'dynamic-akv-switcher',
            bind: 'password',
            label: stringResources.ModelName_Password,
            enableAKVParameterization: true,
            child: {
                type: 'parameterizable',
                attribute: 'password',
                disableAddParameter: true,
                child: {
                    type: 'password',
                    label: stringResources.ModelName_Password,
                    required: true,
                    isLabelVertical: true,
                    validators: [{ type: 'required', errorMessage: '' }]
                }
            },
            description: format(
                stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                format(stringResources.ModelName_LinkedServiceTemplate, stringResources.SapOData_DisplayText),
                stringResources.ModelName_Password
            )
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'clientId',
                label: stringResources.ModelName_ClientId,
                isLabelVertical: true
            }
        }
    ]
};
